import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { makeStyles } from "@mui/styles";
import {
  Avatar,
  Box,
  Button,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SquarehubLoader from "bundles/common/components/SquarehubLoader";
import sh from "bundles/common/utils/sh";
import { useSnackbar } from "notistack";
import EngineeringDefaultProfile from "../../../../app/assets/images/pictures/engineering_default_profile.png";
import ArchitectureDefaultProfile from "../../../../app/assets/images/pictures/architecture_default_profile.png";
import ConstructionDefaultProfile from "../../../../app/assets/images/pictures/construction_default_profile.png";

const NOTE_MAX_LENGTH = 2000;

const getSectorDefaultAvatar = (desiredSector) => {
  if (!desiredSector) return null;

  const sectorMap = {
    engineering: EngineeringDefaultProfile,
    architecture: ArchitectureDefaultProfile,
    construction: ConstructionDefaultProfile,
  };

  return sectorMap[desiredSector] || null;
};

const useStyles = makeStyles((theme) => ({
  modalContent: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: theme.palette.common.white,
    borderRadius: "16px",
    width: "min(520px, calc(100vw - 32px))",
    maxHeight: "min(560px, calc(100vh - 32px))",
    outline: "none",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "18px 20px 0",
  },
  closeBtn: {
    cursor: "pointer",
    color: theme.palette.text?.secondary || "#666",
    "&:hover": { color: theme.palette.text?.primary || "#111" },
  },
  body: {
    padding: "20px",
    overflowY: "auto",
    flex: 1,
  },
  footer: {
    padding: "14px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderTop: `1px solid ${theme.palette.grey?.[200] || "#EEE"}`,
  },
  saveBtn: {
    borderRadius: "999px",
    padding: "10px 28px",
    fontWeight: 800,
    textTransform: "none",
    background: theme.palette.primary?.dark || theme.palette.text?.main || "#0F172A",
    color: theme.palette.common.white,
    "&:hover": {
      background: theme.palette.secondary.hover,
      color: theme.palette.text.main,
    },
    "&:disabled": {
      background: theme.palette.grey?.[300] || "#DDD",
      color: theme.palette.grey?.[500] || "#999",
    },
  },
  cancelBtn: {
    textTransform: "none",
    fontWeight: 700,
    color: theme.palette.text?.secondary || "#666",
    borderRadius: "999px",
    fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
  },
}));

// Add a private (company-scoped) note on a candidate. Same CandidateNote records
// as the Kanban dialog notes, so anything written here shows up there too.
const CandidateNoteModal = ({ open, onClose, candidate, recordType = "Candidate", onNoteCreated }) => {
  const classes = useStyles();
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const authenticityToken = useSelector((state) => state.authenticity_token);

  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [existingNotes, setExistingNotes] = useState([]);

  const candidateName =
    candidate?.full_name || [candidate?.first_name, candidate?.last_name].filter(Boolean).join(" ");
  const candidateTitle = candidate?.job_title || candidate?.desired_job_title;

  useEffect(() => {
    if (!open) {
      setContent("");
      setExistingNotes([]);
      return;
    }
    if (!candidate?.id) return;

    setLoading(true);
    sh.get("candidate_notes", {
      // Own cancel key: sh cancels by URL, and the create POST shares this URL.
      cancelKey: "candidate-note-modal-notes",
      params: { record_type: recordType, record_id: candidate.id },
    })
      .then((res) => setExistingNotes(res.data?.data || []))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [open, candidate?.id, recordType]);

  const handleSave = () => {
    const trimmed = content.trim();
    if (!trimmed || !candidate?.id || saving) return;

    setSaving(true);
    sh.post("candidate_notes", {
      authenticity_token: authenticityToken,
      record_type: recordType,
      record_id: candidate.id,
      content: trimmed,
      pinned: false,
    })
      .then((res) => {
        const createdNote = res.data?.data;
        enqueueSnackbar(intl.formatMessage({ id: "recruiter.kanban.note_added", defaultMessage: "Note added" }), {
          variant: "success",
        });
        if (createdNote && typeof onNoteCreated === "function") onNoteCreated(createdNote);
        onClose();
      })
      .catch((err) => {
        console.error(err);
        enqueueSnackbar(
          intl.formatMessage({ id: "recruiter.kanban.note_add_failed", defaultMessage: "Failed to add note" }),
          { variant: "error" }
        );
      })
      .finally(() => setSaving(false));
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={classes.modalContent}>
        <Box className={classes.header}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
            <Avatar
              src={candidate?.profile_picture_url || getSectorDefaultAvatar(candidate?.desired_sector)}
              alt={candidateName}
              sx={{ width: 40, height: 40, borderRadius: 0, backgroundColor: "#9e9e9e" }}
            >
              {[candidate?.first_name?.[0], candidate?.last_name?.[0]].filter(Boolean).join("")}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight={700} noWrap>
                {candidateName}
              </Typography>
              {candidateTitle && (
                <Typography variant="body2" color="textSecondary" noWrap>
                  {candidateTitle}
                </Typography>
              )}
            </Box>
          </Box>
          <CloseIcon className={classes.closeBtn} onClick={onClose} />
        </Box>

        <Box className={classes.body}>
          {loading ? (
            <SquarehubLoader size={56} centered />
          ) : (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 2 }}>
                <LockOutlinedIcon sx={{ fontSize: "16px", color: "text.secondary" }} />
                <Typography variant="body2" color="textSecondary">
                  <FormattedMessage
                    id="recruiter.kanban.private_notes_hint"
                    defaultMessage="Internal to your team, never visible to the candidate."
                  />
                </Typography>
              </Box>
              {existingNotes.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1,
                      fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                      color: "hsl(153, 68%, 25%)",
                    }}
                  >
                    <FormattedMessage id="recruiter.kanban.private_notes" defaultMessage="Private notes" />
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                    {existingNotes.slice(0, 3).map((note) => (
                      <Box
                        key={note.id}
                        sx={{
                          px: 1.5,
                          py: 0.75,
                          bgcolor: "hsl(160, 10%, 96%)",
                          color: "#023331",
                          borderRadius: "12px",
                        }}
                      >
                        <Typography variant="body2" sx={{ color: "inherit", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                          {note.content}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "inherit", opacity: 0.7 }}>
                          {note.recruiter_name
                            ? `${note.recruiter_name} · `
                            : ""}
                          {new Date(note.created_at).toLocaleString("fr-FR", {
                            day: "numeric",
                            month: "short",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              <TextField
                fullWidth
                multiline
                minRows={4}
                maxRows={8}
                value={content}
                onChange={(event) => setContent(event.target.value)}
                inputProps={{ maxLength: NOTE_MAX_LENGTH }}
                placeholder={intl.formatMessage({
                  id: "recruiter.kanban.note_placeholder",
                  defaultMessage: "Write a private note...",
                })}
                disabled={saving}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                    handleSave();
                  }
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    "& fieldset": { borderColor: "hsl(160, 10%, 90%)" },
                    "&.Mui-focused fieldset": { borderColor: "hsl(153, 68%, 25%)" },
                  },
                }}
              />
            </>
          )}
        </Box>

        <Box className={classes.footer}>
          <Button className={classes.cancelBtn} onClick={onClose} disabled={saving}>
            <FormattedMessage id="cancel" />
          </Button>
          <Button
            className={classes.saveBtn}
            onClick={handleSave}
            disabled={!content.trim() || saving || loading}
          >
            {saving ? (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 24 }}>
                <SquarehubLoader size={22} />
              </Box>
            ) : (
              <FormattedMessage id="recruiter.kanban.save" defaultMessage="Save" />
            )}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default CandidateNoteModal;
